import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  type IconSize,
} from "@salestrak/ui";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import type { WidgetRefresh } from "#app/widgets/dashboards/use-widget-refresh";

// A successful refresh stays quiet until its age crosses this threshold.
const STALE_REFRESH_MS = 10 * 60_000;

/**
 * The refresh action's glyph — the ONE place the state machine becomes an
 * icon, for the free toolbar's control and both of the detail dialog's refresh
 * actions. `reload` at rest, spinning while the remounted queries are in
 * flight; then the outcome takes the glyph's place — a success-colored check
 * once every query settled clean, a destructive alert when one settled with an
 * error — until the state resets and `reload` returns. The button stays the
 * same control throughout; only its face reports, so no second mark sits
 * beside it.
 */
export function WidgetRefreshIcon({
  refresh,
  size,
}: {
  refresh: WidgetRefresh;
  size: IconSize;
}) {
  switch (refresh.state) {
    case "refreshing":
      return <Icon name="reload" size={size} animation="spin" />;
    case "success":
      return <Icon name="check-circle" size={size} color="success" />;
    case "failed":
      return <Icon name="alert-circle" size={size} color="destructive" />;
    default:
      return <Icon name="reload" size={size} />;
  }
}

/**
 * The status text beside every refresh action — the free toolbar's
 * board-level control and the detail dialog's header. Reads the shared state
 * machine (`useWidgetRefresh`): "Refreshing..." while the remounted queries
 * are in flight, "Success!" once every one has settled clean, "Refresh
 * failed" when any settled with an error (the glyph in the button carries the
 * matching mark — see `WidgetRefreshIcon`), and otherwise quiet — "Refreshed:
 * <relative>" appears only once the last successful refresh has gone stale.
 * Hidden on mobile, where neither host has room for prose.
 */
export function WidgetRefreshStatus({ refresh }: { refresh: WidgetRefresh }) {
  const isMobile = useIsMobile();
  const { state, lastRefreshedAt } = refresh;
  // Re-render every 30s so a stale status appears and ages live even when
  // nothing else changes.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  if (isMobile) return null;
  if (state === "refreshing") {
    return (
      <Text size="xs" variant="muted" truncate={1}>
        Refreshing...
      </Text>
    );
  }
  if (state === "success") {
    return (
      <Text size="xs" variant="success" truncate={1}>
        Success!
      </Text>
    );
  }
  if (state === "failed") {
    return (
      <Text size="xs" variant="destructive" truncate={1}>
        Refresh failed
      </Text>
    );
  }
  if (Date.now() - lastRefreshedAt <= STALE_REFRESH_MS) return null;
  return (
    <Text size="xs" variant="muted" truncate={1}>
      Refreshed: {formatRelativeDate(new Date(lastRefreshedAt).toISOString(), "long")}
    </Text>
  );
}

/**
 * Board-level refresh — leads the free toolbar's data-window cluster because
 * it re-runs exactly the window the sibling chips define. The Dashboard host
 * owns the state machine (`useWidgetRefresh`: its `tick` keys the board's
 * `WidgetRefreshBoundary`, which reports the remount so the hook can watch
 * the re-run queries settle); this control renders the handle — the status
 * text (`WidgetRefreshStatus`), then the icon button, whose glyph reports the
 * state (`WidgetRefreshIcon`) and stays pressable in every state but
 * refreshing.
 */
export function DashboardRefreshControl({ refresh }: { refresh: WidgetRefresh }) {
  const refreshing = refresh.state === "refreshing";
  return (
    <Box direction="row" align="center" gap="xs">
      <WidgetRefreshStatus refresh={refresh} />
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Refresh all widgets"
              disabled={refreshing}
              onClick={refresh.refresh}
            />
          }
        >
          <WidgetRefreshIcon refresh={refresh} size="sm" />
        </TooltipTrigger>
        <TooltipPopup side="bottom">
          <Text size="xs">Refresh all widgets</Text>
        </TooltipPopup>
      </Tooltip>
    </Box>
  );
}
